import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { useState } from 'react'

interface FAQItem {
  question: string
  answer: string
  category: string
}

const faqData: FAQItem[] = [
  {
    category: "Getting Started",
    question: "What is Sportspot?",
    answer: "Sportspot is a mobile app and web platform that gamifies spotting sports team merchandise in the wild. You can track team apparel, compete with friends, and build your collection of spotted teams from major leagues like MLB, NBA, NFL, NHL, MLS, and international soccer."
  },
  {
    category: "Getting Started",
    question: "How do I spot my first team?",
    answer: "After signing up, simply navigate to the 'Browse Teams' section, find the team whose merchandise you've spotted, and tap the 'Spot' button. You can add location details and notes about where you saw the team gear."
  },
  {
    category: "Getting Started",
    question: "What counts as a valid team spot?",
    answer: "Any official team merchandise counts including jerseys, hats, t-shirts, hoodies, stickers, car decals, flags, and other licensed team apparel or accessories. The item should clearly display team logos, colors, or branding."
  },
  {
    category: "Teams & Leagues",
    question: "Which leagues are supported?",
    answer: "We currently support MLB, NBA, NFL, NHL, MLS, Premier League, La Liga, Serie A, Bundesliga, Ligue 1, NWSL, and WNBA. We're continuously adding more leagues and teams based on user feedback."
  },
  {
    category: "Teams & Leagues",
    question: "What if I can't find a team I spotted?",
    answer: "If you can't find a specific team, please contact our support team at support@sportspot.com. We regularly update our database and add new teams based on user requests."
  },
  {
    category: "Teams & Leagues",
    question: "Can I spot the same team multiple times?",
    answer: "Yes! You can spot the same team multiple times in different locations or on different occasions. Each spot is tracked separately and adds to your overall collection statistics."
  },
  {
    category: "Competitions & Trips",
    question: "How do trip competitions work?",
    answer: "Trip competitions allow you to compete with friends during vacations or road trips. Create a competition, invite friends, set a time period, and see who can spot the most teams during your journey. Points are awarded based on team rarity and location diversity."
  },
  {
    category: "Competitions & Trips",
    question: "How is scoring calculated in competitions?",
    answer: "Scoring is based on several factors including team rarity, geographic diversity, and timing. Rarer teams and teams spotted in unexpected locations may earn bonus points. The exact scoring algorithm is designed to keep competitions fair and exciting."
  },
  {
    category: "Competitions & Trips",
    question: "Can I join a competition after it has started?",
    answer: "This depends on the competition settings chosen by the creator. Some competitions allow late joiners while others are locked once started. Check with the competition organizer for specific rules."
  },
  {
    category: "Account & Privacy",
    question: "Is my location data shared with others?",
    answer: "Your exact location data is never shared with other users without your explicit consent. You can choose to share general location information (like city or region) in competitions, but precise GPS coordinates remain private."
  },
  {
    category: "Account & Privacy",
    question: "Can I make my profile private?",
    answer: "Yes, you can adjust your privacy settings in the Settings menu. You can control who can see your spotted teams, competition results, and profile information."
  },
  {
    category: "Account & Privacy",
    question: "How do I delete my account?",
    answer: "To delete your account, go to Settings > Account > Delete Account. This will permanently remove all your data including spotted teams, competition history, and profile information. This action cannot be undone."
  },
  {
    category: "Technical Support",
    question: "The app isn't working properly. What should I do?",
    answer: "First, try restarting the app and checking your internet connection. If problems persist, try logging out and back in. For ongoing issues, contact our support team at support@sportspot.com with details about your device and the specific problem."
  },
  {
    category: "Technical Support",
    question: "Why can't I log in to my account?",
    answer: "Common login issues include incorrect email/password, network connectivity problems, or account suspension. Try resetting your password, check your internet connection, and ensure you're using the correct email address. Contact support if issues persist."
  },
  {
    category: "Technical Support",
    question: "Can I use Sportspot on multiple devices?",
    answer: "Yes! Your account syncs across all devices. Simply log in with the same credentials on any device to access your complete spotting history and collections."
  }
]

export default function FAQPage() {
  const [openItems, setOpenItems] = useState<number[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>("All")

  const categories = ["All", ...Array.from(new Set(faqData.map(item => item.category)))]
  
  const filteredFAQs = selectedCategory === "All" 
    ? faqData 
    : faqData.filter(item => item.category === selectedCategory)

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    )
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <img 
                src="/images/sportspot-transparent-bg.png" 
                alt="Sportspot Logo" 
                className="h-8 sm:h-12 w-auto"
              />
            </div>
            <div>
              <a href="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h1>
          
          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-[#42a5f5] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left bg-white hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <div>
                    <span className="text-xs text-[#42a5f5] font-medium mb-1 block">
                      {faq.category}
                    </span>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {faq.question}
                    </h3>
                  </div>
                  {openItems.includes(index) ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 flex-shrink-0 ml-4" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0 ml-4" />
                  )}
                </button>
                
                {openItems.includes(index) && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Support */}
          <div className="mt-16 bg-[#42a5f5] rounded-lg p-8 text-white text-center">
            <h2 className="text-2xl font-semibold mb-4">Still have questions?</h2>
            <p className="mb-6">Can't find the answer you're looking for? Our support team is here to help.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact" 
                className="bg-white text-[#42a5f5] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Contact Support
              </a>
              <a 
                href="/help" 
                className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-[#42a5f5] transition-colors"
              >
                Help Center
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
